import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { HiMail, HiShieldCheck, HiEye, HiEyeOff } from 'react-icons/hi';
import { API_URL } from '../../config/apiConfig';
import { getPasswordEndpoints } from '../../utils/passwordEndpoints';

const ResetPassword = ({ role }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = location.state?.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !otp || !newPassword) return toast.error('Please fill all fields');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');

    setLoading(true);
    try {
      const computedRole = role || (typeof window !== 'undefined' ? (window.location.pathname.split('/')[1] || 'user') : 'user');
      const endpoints = getPasswordEndpoints(computedRole);
      const res = await axios.post(endpoints.resetUrl, { email, otp, newPassword });
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Password reset successful');
        navigate(endpoints.loginUrl);
      } else {
        toast.error(res.data?.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error resetting password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-container min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="reset-card max-w-md w-full bg-white p-6 rounded-lg shadow-md border border-gray-100">
        <h2 className="reset-title text-2xl font-semibold mb-2">Reset Password</h2>
        <p className="reset-subtitle text-sm text-gray-600 mb-6">Enter the OTP sent to your email and choose a new password.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-field">
            <label className="sr-only">Email</label>
            <div className="input-with-icon relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-indigo-500">
                <HiMail className="h-5 w-5" />
              </div>
              <input
                type="email"
                className="w-full pl-12 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-field">
            <label className="sr-only">OTP</label>
            <div className="input-with-icon relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-amber-500">
                <HiShieldCheck className="h-5 w-5" />
              </div>
              <input
                type="text"
                className="w-full pl-12 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                placeholder="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-field">
            <label className="sr-only">New password</label>
            <div className="input-with-icon relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button
                type="button"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowNewPassword((s) => !s)}
                className="toggle-visibility-btn absolute inset-y-0 right-2 flex items-center px-2 text-gray-600 hover:text-gray-900 focus:outline-none"
              >
                {showNewPassword ? <HiEyeOff className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="form-field">
            <label className="sr-only">Confirm new password</label>
            <div className="input-with-icon relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                onClick={() => setShowConfirmPassword((s) => !s)}
                className="toggle-visibility-btn absolute inset-y-0 right-2 flex items-center px-2 text-gray-600 hover:text-gray-900 focus:outline-none"
              >
                {showConfirmPassword ? <HiEyeOff className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`w-full py-3 rounded-lg flex items-center justify-center space-x-2 ${loading ? 'bg-gray-400' : 'bg-indigo-600 text-white'}`}
            disabled={loading}
          >
            <span className="inline-flex items-center">
              <HiShieldCheck className="h-5 w-5 mr-2 text-white" />
            </span>
            <span>{loading ? 'Resetting...' : 'Reset Password'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
